import { useTranslate } from '@refinedev/core';
import { Space, Tag, Typography } from 'antd';
import { SeriesChart } from '../../components/charts/SeriesChart';
import {
  activeSeriesOf,
  asleepSeriesOf,
  hoursAndMinutes,
  trendAverages,
  type CheckInPhoneAveragesRow,
  type CheckInSharedTypesRow,
  type RecapPhone,
  type TrendPhone,
} from './checkInPhone';

type Translate = ReturnType<typeof useTranslate>;

const sleepAverageText = (t: Translate, avg: CheckInPhoneAveragesRow): string | null =>
  avg.asleepMinutes == null
    ? null
    : avg.efficiency != null
    ? t('health.checkIns.sleepAverage', {
        duration: t('health.sleep.duration', hoursAndMinutes(avg.asleepMinutes)),
        percent: avg.efficiency,
      })
    : t('health.checkIns.sleepAverageNoEfficiency', {
        duration: t('health.sleep.duration', hoursAndMinutes(avg.asleepMinutes)),
      });

const activeAverageText = (t: Translate, avg: CheckInPhoneAveragesRow): string | null =>
  avg.activeMinutes == null ? null : t('health.checkIns.activeAverage', { count: avg.activeMinutes });

/**
 * E43 · "Client Hub — Check-ins (trend)" (cc-assessments, the screen's Behavior): "the phone's sleep and active minutes
 * appear as two more rows under the mood line only for the types the client shares with this therapist (default off);
 * 'not shared' otherwise — the self-reported sleep from the check-in stays its own row". Hours asleep by the day the
 * client woke, active minutes by day; numbers only.
 */
export const CheckInPhoneRows = ({ phone, locale }: { phone: TrendPhone; locale: 'en' | 'ru' }) => {
  const t = useTranslate();
  const shared: CheckInSharedTypesRow = phone.sharedTypes ?? { sleep: false, activity: false };
  const avg = trendAverages(phone);
  const row = (
    kind: 'sleep' | 'activity',
    label: string,
    isShared: boolean,
    hasData: boolean,
    average: string | null,
    chart: () => JSX.Element,
  ) => (
    <Space direction="vertical" size={2} style={{ width: '100%' }} data-testid={`checkin-hub-phone-${kind}`}>
      <Space size="small" wrap>
        <Typography.Text strong>{label}</Typography.Text>
        {!isShared ? (
          <Tag data-testid={`checkin-hub-phone-${kind}-not-shared`}>{t('health.checkIns.notShared')}</Tag>
        ) : !hasData ? (
          <Typography.Text type="secondary" data-testid={`checkin-hub-phone-${kind}-empty`}>
            {t('health.hub.noDataYet')}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary" data-testid={`checkin-hub-phone-${kind}-average`}>
            {average}
          </Typography.Text>
        )}
      </Space>
      {isShared && hasData ? chart() : null}
    </Space>
  );
  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }} data-testid="checkin-hub-phone">
      <Typography.Text type="secondary">{t('health.checkIns.rowsTitle')}</Typography.Text>
      {row('sleep', t('health.checkIns.asleepRow'), shared.sleep, !!phone.sleepNights?.length, sleepAverageText(t, avg), () => (
        <SeriesChart
          ariaLabel={t('health.checkIns.asleepRow')}
          height={140}
          locale={locale}
          series={asleepSeriesOf(phone.sleepNights ?? []).map((s) => ({ ...s, label: t('health.checkIns.asleepRow') }))}
          testId="checkin-hub-phone-sleep-chart"
        />
      ))}
      {row('activity', t('health.checkIns.activeRow'), shared.activity, !!phone.activityDays?.length, activeAverageText(t, avg), () => (
        <SeriesChart
          ariaLabel={t('health.checkIns.activeRow')}
          height={140}
          locale={locale}
          series={activeSeriesOf(phone.activityDays ?? []).map((s) => ({ ...s, label: t('health.checkIns.activeRow') }))}
          testId="checkin-hub-phone-activity-chart"
        />
      ))}
    </Space>
  );
};

/**
 * E43 · "Session form — Check-in recap" (the screen's Behavior): "the recap adds, for the types the client shares with
 * this therapist, the phone's average sleep and active minutes since the last session beside the check-in averages".
 * Nothing at all while the client shares neither with this therapist (the hub's tab says "not shared").
 */
export const CheckInPhoneRecap = ({ phone }: { phone: RecapPhone }) => {
  const t = useTranslate();
  const shared: CheckInSharedTypesRow = phone.sharedTypes ?? { sleep: false, activity: false };
  if (!shared.sleep && !shared.activity) return null;
  const avg = phone.phoneAverages ?? {};
  const line = (kind: 'sleep' | 'activity', isShared: boolean, text: string | null) => (
    <span data-testid={`checkin-recap-phone-${kind}`}>
      {!isShared
        ? t('health.checkIns.recapNotShared', { type: t(`health.types.${kind}`) })
        : text ?? t('health.hub.noDataYet')}
    </span>
  );
  return (
    <Space direction="vertical" size={4} data-testid="checkin-recap-phone">
      <Typography.Text strong>{t('health.checkIns.recapTitle')}</Typography.Text>
      {line('sleep', shared.sleep, sleepAverageText(t, avg))}
      {line('activity', shared.activity, activeAverageText(t, avg))}
    </Space>
  );
};
